import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { Invoice } from "~/shared/schemas/invoice-schema";
import type {
	PaymentNonce,
	SavedAch,
	SavedCard,
} from "~/shared/types/payment-types.ts";

import { useGetCustomerAccounts } from "./use-get-customer-accounts.ts";
import { usePostSubmitPayment } from "./use-post-payment.ts";

type PaymentData = {
	amounts: number;
	invoiceAmounts: Record<string, number>;
	description: string;
	isLumpSum: boolean;
};

export function usePaymentExecution() {
	const { showError, showSuccess } = useNotificationActions();
	const queryClient = useQueryClient();
	const { submitPaymentAsync, isSubmitting } = usePostSubmitPayment();
	const { accounts } = useGetCustomerAccounts();

	const executePayment = useCallback(
		async (
			selectedInvoices: Invoice[],
			selectedPaymentValue: SavedCard | SavedAch | PaymentNonce | null,
			paymentData: PaymentData,
			onSuccess: (result: unknown) => void,
		) => {
			if (!selectedPaymentValue) {
				console.error("No payment method selected.");
				return null;
			}

			try {
				const transactionDetails = selectedInvoices.map((invoice) => ({
					invoiceID: invoice.invoiceID,
					poNumber: invoice.poNumber,
					tqlDocumentNumber: invoice.tqlDocumentNumber,
					invoiceType: invoice.invoiceType,
					amtPaid:
						paymentData.invoiceAmounts[String(invoice.invoiceID)] ??
						paymentData.amounts,
				}));

				const payload = {
					paymentMerchantCustomerId: accounts?.paymentMerchantCustomerId,
					paymentMethodToken: selectedPaymentValue.paymentMethodToken,
					appSourceType: "New Trax",
					paymentMethod: selectedPaymentValue.paymentMethod === "ACH" ? 1 : 2,
					paymentAccountLastFourDigits: selectedPaymentValue.Last4,
					totalPaymentAmount: paymentData.amounts,
					paymentComments: paymentData.description,
					transactionDetails: paymentData.isLumpSum
						? [
								{
									amtPaid: paymentData.amounts,
									invoiceID: "",
									invoiceType: 3,
									poNumber: 0,
								},
							]
						: transactionDetails,
				};

				const result = await submitPaymentAsync(payload);

				showSuccess("Payment submitted successfully.");
				onSuccess(result);

				void Promise.all([
					queryClient.invalidateQueries({ queryKey: ["customerInvoices"] }),
					queryClient.invalidateQueries({ queryKey: ["invoice-summary"] }),
				]).catch((refreshError) => {
					console.error("Post-payment cache refresh failed:", refreshError);
				});

				return result;
			} catch (error_) {
				console.error("Failed to execute payment:", error_);
				showError("Failed to submit payment. Please try again.");
				return null;
			}
		},
		[
			accounts?.paymentMerchantCustomerId,
			submitPaymentAsync,
			showError,
			showSuccess,
			queryClient,
		],
	);

	return { executePayment, isSubmitting };
}
